import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Activity, ArrowRight, Boxes, Cable, Database, Link2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAppStore } from '@/store/appStore';
import {
  createAppKey,
  createAdminUser,
  deleteAppInstance,
  getAppInstance,
  getAppSettings,
  listAppMembers,
  listAppAuditEvents,
  listAppInstances,
  removeAppMember,
  retryProvisioning,
  revokeAppKey,
  upsertAppMember,
  updateAppInstance,
  updateAppSettings,
  setAppDedicatedMongoUri,
  testMongoConnection,
} from './api';
import { McpConnectionCard } from './components/McpConnectionCard';
import { AppLifecyclePanel } from './components/AppLifecyclePanel';
import { AppHostingCard } from './components/AppHostingCard';
import { MailboxPanel } from './components/MailboxPanel';
import { ProvisioningStatusCard } from './components/ProvisioningStatusCard';
import { CreateAgentModal } from './components/CreateAgentModal';
import { AgentAllowlistEditor } from './components/AgentAllowlistEditor';
import { AppCreateForm } from './components/AppCreateForm';
import ObservabilityPanel from './components/ObservabilityPanel';
import BuildSessionsTracker from './components/BuildSessionsTracker';
import McpHandoffCard from './components/McpHandoffCard';
import { AppDataSourcesSummaryCard } from './components/AppDataSourcesSummaryCard';
import { AppApiConnectionsCard } from './components/AppApiConnectionsCard';
import { useNotifications } from '@/features/agent-actions/hooks/useAgentActions';
import { isAppActive, isSandboxExpired } from './appInstanceStatus';

type StorageMode = 'shared' | 'dedicated';

const STORAGE_MODE_OPTIONS: Array<{ id: StorageMode; label: string; description?: string }> = [
  { id: 'shared', label: 'Internal (platform managed)', description: '(default)' },
  { id: 'dedicated', label: '3rd-party MongoDB' },
];

function validateWorkflowOverrides(workflowOverrides: any) {
  if (workflowOverrides == null) return [];
  if (typeof workflowOverrides !== 'object' || Array.isArray(workflowOverrides)) return ['workflow_overrides must be a JSON object'];

  const errors: string[] = [];
  const keyRe = /^(?:\*|[A-Za-z0-9._-]+):(?:\*|[A-Za-z0-9._-]+)$/;
  const allowedFields = new Set(['executions_per_minute', 'executions_per_day', 'tokens_per_day']);

  for (const [k, v] of Object.entries(workflowOverrides)) {
    if (!keyRe.test(String(k))) {
      errors.push(`Invalid override key "${k}" (expected "<agent_id>:<workflow>", wildcards "*" allowed)`);
      continue;
    }
    if (v == null || typeof v !== 'object' || Array.isArray(v)) {
      errors.push(`Override "${k}" must be an object`);
      continue;
    }
    for (const [field, raw] of Object.entries(v as any)) {
      if (!allowedFields.has(String(field))) {
        errors.push(`Override "${k}" has unknown field "${field}"`);
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        errors.push(`Override "${k}.${field}" must be a positive number (> 0)`);
      }
    }
  }

  return errors;
}

const DEFAULT_AGENT_QUOTAS = {
  executions_per_minute: 60,
  executions_per_day: 2000,
  tokens_per_day: 50000,
};


function StatusBadge({ status }: { status: string }) {
  const s = String(status || '').toLowerCase();
  const variant = s === 'active' ? 'default' : (s === 'failed' || s === 'expired') ? 'destructive' : 'secondary';
  return (
    <Badge variant={variant as any} className="text-xs">
      {status || 'unknown'}
    </Badge>
  );
}

function StagedDeletionBadge({ app }: { app: { staged_for_deletion?: boolean; hard_delete_scheduled?: string | null; metadata?: any } }) {
  const date =
    app.hard_delete_scheduled ||
    app.metadata?.hard_delete_scheduled ||
    (app.staged_for_deletion ? '2026-07-27' : null);
  if (!app.staged_for_deletion && !date) return null;
  const label = `Staged for deletion — hard delete ${String(date).slice(0, 10)}`;
  return (
    <Badge
      variant="outline"
      className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-900 border-amber-300 font-medium"
      title={label}
    >
      {label}
    </Badge>
  );
}

type AppDeleteTarget = {
  appId: string;
  appName: string;
};

function AppDeleteConfirmationDialog({
  open,
  target,
  onOpenChange,
  onConfirm,
  isPending,
  errorMessage,
}: {
  open: boolean;
  target: AppDeleteTarget | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
  errorMessage?: string | null;
}) {
  const [confirmationText, setConfirmationText] = useState('');
  const confirmationValue = (target?.appName || target?.appId || '').trim();
  const appLabel = target?.appName || target?.appId || 'this app';
  const isMatch = confirmationValue.length > 0 && confirmationText.trim() === confirmationValue;

  useEffect(() => {
    setConfirmationText('');
  }, [open, target?.appId]);

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (!isPending) onOpenChange(next);
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete app instance</DialogTitle>
          <DialogDescription>
            This will delete the app instance from normal use and revoke its active keys. Type the app name exactly to continue.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
            Type <code className="rounded bg-white/80 px-1 py-0.5">{confirmationValue}</code> to confirm deletion of <strong>{appLabel}</strong>.
          </div>
          {target?.appId && target?.appName && target.appName !== target.appId && (
            <div className="text-xs text-gray-600">
              App ID: <code>{target.appId}</code>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-700">App name confirmation</label>
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={confirmationValue}
              autoFocus
            />
          </div>
          {errorMessage && <div className="text-sm text-red-600">{errorMessage}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!isMatch || isPending}>
            {isPending ? 'Deleting…' : 'Delete app'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppList() {
  const navigate = useNavigate();
  const setApp = useAppStore((s) => s.setApp);
  const [search, setSearch] = useState('');
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLogoUrl, setEditLogoUrl] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<AppDeleteTarget | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  /** Default false — hide kill-list / inactive debris so portfolio reads honestly (UI truth F1). */
  const [showInactive, setShowInactive] = useState(false);

  const q = useQuery({
    queryKey: ['app-instances', 'list'],
    queryFn: listAppInstances,
    staleTime: 10_000,
  });

  const notifQ = useNotifications();
  const notifCountByApp = useMemo(() => {
    const map = new Map<string, number>();
    for (const n of notifQ.data?.notifications || []) {
      if (!n.app_id) continue;
      map.set(n.app_id, (map.get(n.app_id) || 0) + 1);
    }
    return map;
  }, [notifQ.data?.notifications]);

  const update = useMutation({
    mutationFn: async () => {
      if (!editingAppId) throw new Error('missing_app_id');
      return updateAppInstance(editingAppId, {
        name: editName.trim() || undefined,
        description: editDescription.trim() || undefined,
        logo_url: editLogoUrl.trim() || undefined,
      });
    },
    onSuccess: () => {
      q.refetch();
      setEditingAppId(null);
    },
  });

  const remove = useMutation({
    mutationFn: async (app_id: string) => deleteAppInstance(app_id),
    onSuccess: () => {
      setDeleteTarget(null);
      q.refetch();
    },
  });

  const apps = Array.isArray(q.data?.apps) ? q.data!.apps : [];
  const inactiveCount = useMemo(
    () => apps.filter((a) => !isAppActive(a)).length,
    [apps],
  );
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return apps.filter((a) => {
      if (!showInactive && !isAppActive(a)) return false;
      if (!s) return true;
      return `${a.app_id} ${a.name} ${a.status} ${a.description || ''}`.toLowerCase().includes(s);
    });
  }, [apps, search, showInactive]);

  const stats = useMemo(() => {
    const portfolio = apps.filter((a) => isAppActive(a));
    const active = portfolio.length;
    const mcpConnected = portfolio.filter((a) => Boolean((a as any)?.mcp_summary?.provisioned)).length;
    const mcpReady = portfolio.filter((a) => Boolean((a as any)?.mcp_summary?.provisioned)).length;
    const linkedDataReady = portfolio.filter((a) => {
      const activeModules = Array.isArray((a as any)?.module_summary?.active) ? (a as any).module_summary.active : [];
      return activeModules.some((m: string) => {
        const label = String(m || '').toLowerCase();
        return label.includes('asset') || label.includes('mapper');
      });
    }).length;
    return {
      total: showInactive ? apps.length : active,
      active,
      mcpConnected,
      mcpReady,
      linkedDataReady,
    };
  }, [apps, showInactive]);

  const StatTile = ({
    title,
    value,
    subtitle,
    icon: Icon,
    tone = 'slate',
  }: {
    title: string;
    value: number;
    subtitle: string;
    icon: React.ComponentType<{ className?: string }>;
    tone?: 'slate' | 'blue' | 'emerald' | 'amber' | 'indigo';
  }) => (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
          <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
        </div>
        <div
          className={`rounded-lg p-2 ${
            tone === 'blue'
              ? 'bg-blue-100'
              : tone === 'emerald'
                ? 'bg-emerald-100'
                : tone === 'amber'
                  ? 'bg-amber-100'
                  : tone === 'indigo'
                    ? 'bg-indigo-100'
                    : 'bg-slate-100'
          }`}
        >
          <Icon
            className={`h-4 w-4 ${
              tone === 'blue'
                ? 'text-blue-700'
                : tone === 'emerald'
                  ? 'text-emerald-700'
                  : tone === 'amber'
                    ? 'text-amber-700'
                    : tone === 'indigo'
                      ? 'text-indigo-700'
                      : 'text-slate-700'
            }`}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="p-4 space-y-4 w-full">
        <div className="rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                App Instances Control Center
              </div>
              <div className="text-sm text-gray-600 mt-2">
                Manage backend builder apps, linked data modules, and Cursor MCP handoff from one place.
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => navigate('/api-manager')}>
                View Platform APIs
              </Button>
              <Button variant="outline" onClick={() => navigate('/apps')}>
                App Registry
              </Button>
              <Button variant="outline" onClick={() => q.refetch()} disabled={q.isFetching} className="shadow-sm">
                {q.isFetching ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <Badge variant="secondary" className="px-2 py-1">Dashboard</Badge>
            <Badge variant="outline" className="px-2 py-1">Registry</Badge>
            <Badge variant="outline" className="px-2 py-1">Builder</Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          <StatTile title="Workspaces" value={stats.total} subtitle={showInactive ? 'All non-deleted instances' : 'Active portfolio'} icon={Boxes} tone="slate" />
          <StatTile title="Active" value={stats.active} subtitle="status=active, not expired — not a health claim" icon={Activity} tone="blue" />
          <StatTile title="MCP Connected" value={stats.mcpConnected} subtitle="Provisioned MCP keys" icon={Cable} tone="emerald" />
          <StatTile title="MCP Ready" value={stats.mcpReady} subtitle="Provisioned + MCP key" icon={Cable} tone="indigo" />
          <StatTile title="Linked Data Ready" value={stats.linkedDataReady} subtitle="Mapper or Asset active" icon={Database} tone="amber" />
        </div>

        {showCreateForm ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-bold text-gray-900">Create App Instance</div>
              <Button variant="outline" onClick={() => setShowCreateForm(false)} className="shadow-sm">
                Cancel
              </Button>
            </div>
            <AppCreateForm
              onCreated={(resp) => {
                setShowCreateForm(false);
                q.refetch();
                if (resp?.app_id) {
                  setApp({ appId: String(resp.app_id), appName: resp?.name || null });
                  navigate(`/apps/${resp.app_id}`);
                }
              }}
            />
          </div>
        ) : (
          <div className="flex justify-end">
            <Button
              onClick={() => setShowCreateForm(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md"
            >
              Create App Instance
            </Button>
          </div>
        )}

        <Card className="shadow-lg border-gray-200">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-lg font-bold text-gray-900">Your Workspaces</div>
              <div className="flex flex-wrap items-center gap-2">
                {inactiveCount > 0 && (
                  <Button
                    type="button"
                    variant={showInactive ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs shadow-sm"
                    onClick={() => setShowInactive((v) => !v)}
                  >
                    {showInactive ? 'Hide inactive' : `Show inactive (${inactiveCount})`}
                  </Button>
                )}
                <div className="w-full max-w-xs min-w-[200px]">
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by app name, id, or status..."
                    className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {q.isLoading && <div className="text-sm text-gray-600">Loading apps…</div>}
            {q.error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                {(q.error as any)?.message || 'Failed to load apps'}
              </div>
            )}

            {!q.isLoading && !q.error && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((a: any) => {
                  const activeModules = Array.isArray(a?.module_summary?.active) ? a.module_summary.active : [];
                  const mcpProvisioned = Boolean(a?.mcp_summary?.provisioned);
                  const moduleCount = Number(a?.module_summary?.active_count || activeModules.length || 0);
                  const hasAssetModule = activeModules.some((m: string) => String(m || '').toLowerCase().includes('asset'));
                  const hasMapperModule = activeModules.some((m: string) => String(m || '').toLowerCase().includes('mapper'));
                  const linkedDataReady = activeModules.some((m: string) => {
                    const label = String(m || '').toLowerCase();
                    return label.includes('asset') || label.includes('mapper');
                  });
                  const isSandbox = a?.environment === 'sandbox';
                  const sandboxConfig = a?.sandbox_config;
                  const sandboxExpired = isSandbox && sandboxConfig?.expires_at
                    ? new Date(sandboxConfig.expires_at) < new Date()
                    : false;

                  return (
                    <Card
                      key={a.app_id}
                      className={`h-full hover:shadow-lg transition-all duration-200 cursor-pointer flex flex-col overflow-hidden ${
                        isSandbox
                          ? 'border-2 border-dashed border-amber-400 bg-amber-50/30'
                          : `border-slate-200 ${mcpProvisioned ? 'ring-1 ring-emerald-100' : ''}`
                      }`}
                      onClick={() => {
                        if (editingAppId === a.app_id) return;
                        setApp({ appId: a.app_id, appName: a.name });
                        navigate(`/apps/${a.app_id}`);
                      }}
                    >
                      {a.logo_url && editingAppId !== a.app_id && (
                        <div
                          className="h-24 w-full bg-cover bg-center border-b border-slate-100"
                          style={{ backgroundImage: `url(${a.logo_url})` }}
                        />
                      )}
                      <CardContent className="p-4 space-y-3 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-bold text-xl truncate text-slate-800">{a.name}</div>
                              {(notifCountByApp.get(a.app_id) || 0) > 0 && (
                                <span
                                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold"
                                  title={`${notifCountByApp.get(a.app_id)} pending notification(s)`}
                                >
                                  {notifCountByApp.get(a.app_id)}
                                </span>
                              )}
                              {isSandbox && (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold tracking-wide ${
                                  sandboxExpired
                                    ? 'bg-red-100 text-red-700 border border-red-300'
                                    : 'bg-amber-100 text-amber-800 border border-amber-300'
                                }`}>
                                  {sandboxExpired ? 'SANDBOX EXPIRED' : 'SANDBOX'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 font-mono mt-0.5">{a.app_id}</div>
                            {isSandbox && sandboxConfig && (
                              <div className="mt-1 text-xs text-amber-700 space-y-0.5">
                                <div>Source: <span className="font-mono">{sandboxConfig.source_app_id}</span></div>
                                {sandboxConfig.expires_at && (
                                  <div className={sandboxExpired ? 'text-red-600 font-semibold' : ''}>
                                    {sandboxExpired ? 'Expired' : 'Expires'}: {new Date(sandboxConfig.expires_at).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <StatusBadge status={sandboxExpired ? 'expired' : String(a.status || 'unknown')} />
                            <StagedDeletionBadge app={a} />
                          </div>
                        </div>

                        {editingAppId === a.app_id ? (
                          <div className="space-y-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="App name"
                              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                            <Input
                              value={editLogoUrl}
                              onChange={(e) => setEditLogoUrl(e.target.value)}
                              placeholder="Logo/Cover Image URL (optional)"
                              className="border-gray-300 focus:border-blue-500 focus:ring-blue-500 text-sm"
                            />
                            <Textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder="Short description"
                              className="text-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                            />
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  update.mutate();
                                }}
                                disabled={update.isPending}
                              >
                                {update.isPending ? 'Saving…' : 'Save'}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingAppId(null);
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2 flex-1">
                            <div className="grid grid-cols-3 gap-1">
                              <div className="rounded-md border bg-slate-50 px-2 py-1">
                                <div className="text-[10px] text-gray-500">Modules</div>
                                <div className="text-xs font-semibold text-slate-800">{moduleCount}</div>
                              </div>
                              <div className="rounded-md border bg-slate-50 px-2 py-1">
                                <div className="text-[10px] text-gray-500">MCP</div>
                                <div className={`text-xs font-semibold ${mcpProvisioned ? 'text-emerald-700' : 'text-amber-700'}`}>
                                  {mcpProvisioned ? 'Ready' : 'Pending'}
                                </div>
                              </div>
                              <div className="rounded-md border bg-slate-50 px-2 py-1">
                                <div className="text-[10px] text-gray-500">Linked</div>
                                <div className={`text-xs font-semibold ${linkedDataReady ? 'text-emerald-700' : 'text-gray-600'}`}>
                                  {linkedDataReady ? 'Enabled' : 'Off'}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs">
                              <span className="text-gray-600">MCP</span>
                              <span className={mcpProvisioned ? 'text-emerald-700' : 'text-gray-500'}>
                                {mcpProvisioned ? 'Connected' : 'Not provisioned'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs">
                              <span className="text-gray-600">Linked data</span>
                              <span className={linkedDataReady ? 'text-emerald-700' : 'text-amber-700'}>
                                {linkedDataReady ? 'Ready' : 'Needs module enablement'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs">
                              <span className="text-gray-600">Asset + Mapper</span>
                              <span className={hasAssetModule && hasMapperModule ? 'text-emerald-700' : 'text-gray-600'}>
                                {hasAssetModule && hasMapperModule ? 'Linked path active' : 'Partial path'}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {activeModules.slice(0, 4).map((moduleName: string) => (
                                <Badge key={moduleName} variant="secondary" className="text-[10px]">
                                  {moduleName}
                                </Badge>
                              ))}
                              {activeModules.length === 0 && (
                                <span className="text-xs text-gray-500">No optional modules active</span>
                              )}
                            </div>
                            {a.description && (
                              <div
                                className="text-xs text-gray-600"
                                style={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {a.description}
                              </div>
                            )}
                            <div className="text-xs text-gray-500">
                              Created: {a.created_at ? new Date(a.created_at as any).toLocaleString() : 'unknown'}
                            </div>

                            <div className="mt-auto pt-2 border-t border-slate-100">
                              <div className="flex items-center justify-between gap-2">
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setApp({ appId: a.app_id, appName: a.name });
                                    navigate(`/apps/${a.app_id}`);
                                  }}
                                >
                                  Open Workspace
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setApp({ appId: a.app_id, appName: a.name });
                                    navigate(`/apps/${a.app_id}`);
                                  }}
                                >
                                  Builder <ArrowRight className="h-3.5 w-3.5 ml-1" />
                                </Button>
                              </div>
                              <div className="flex items-center justify-between gap-2 mt-2">
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setApp({ appId: a.app_id, appName: a.name });
                                    navigate(`/apps/${a.app_id}`);
                                  }}
                                >
                                  <Link2 className="h-3.5 w-3.5 mr-1" />
                                  Linked Data
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setApp({ appId: a.app_id, appName: a.name });
                                    navigate(`/apps/${a.app_id}`);
                                  }}
                                >
                                  <Settings className="h-3.5 w-3.5 mr-1" />
                                  Registry
                                </Button>
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <Button
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingAppId(a.app_id);
                                    setEditName(a.name || '');
                                    setEditDescription((a as any).description || '');
                                    setEditLogoUrl(a.logo_url || '');
                                  }}
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    remove.reset();
                                    setDeleteTarget({ appId: a.app_id, appName: a.name || a.app_id });
                                  }}
                                  disabled={remove.isPending}
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
                {filtered.length === 0 && <div className="text-sm text-gray-600">No apps match your search.</div>}
              </div>
            )}
          </CardContent>
        </Card>
        <AppDeleteConfirmationDialog
          open={Boolean(deleteTarget)}
          target={deleteTarget}
          onOpenChange={(open) => {
            if (open) return;
            remove.reset();
            setDeleteTarget(null);
          }}
          onConfirm={() => {
            if (!deleteTarget) return;
            remove.mutate(deleteTarget.appId);
          }}
          isPending={remove.isPending}
          errorMessage={remove.error ? ((remove.error as any)?.message || 'Delete failed') : null}
        />
      </div>
    </div>
  );
}

function AppDetail() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const app_id = String(appId || '');
  const setApp = useAppStore((s) => s.setApp);
  const [newKeyName, setNewKeyName] = useState('Key');
  const [newKeyScopes, setNewKeyScopes] = useState('*');
  const [rename, setRename] = useState('');
  const [desc, setDesc] = useState('');
  const [memberEmailOrId, setMemberEmailOrId] = useState('');
  const [memberRole, setMemberRole] = useState('viewer');
  const [agentsAllowAll, setAgentsAllowAll] = useState(false);
  const [agentsEnforceAllowlist, setAgentsEnforceAllowlist] = useState(false);
  const [agentsQuotaPerMin, setAgentsQuotaPerMin] = useState('60');
  const [agentsQuotaPerDay, setAgentsQuotaPerDay] = useState('0');
  const [agentsTokensPerDay, setAgentsTokensPerDay] = useState('0');
  const [agentsWorkflowOverridesJson, setAgentsWorkflowOverridesJson] = useState('{}');
  const [agentsAllowlistJson, setAgentsAllowlistJson] = useState('[]');
  const [auditType, setAuditType] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminCreateMessage, setAdminCreateMessage] = useState('');
  const [mapperModuleEnabled, setMapperModuleEnabled] = useState(false);
  const [assetModuleEnabled, setAssetModuleEnabled] = useState(false);
  const [storageMode, setStorageMode] = useState<StorageMode>('shared');
  const [dedicatedMongoUri, setDedicatedMongoUri] = useState('');
  const [extMongoHost, setExtMongoHost] = useState('');
  const [extMongoUser, setExtMongoUser] = useState('');
  const [extMongoPass, setExtMongoPass] = useState('');
  const [extMongoDb, setExtMongoDb] = useState('');
  const [extMongoUseSrv, setExtMongoUseSrv] = useState(true);
  const [showExtMongoPass, setShowExtMongoPass] = useState(false);
  const [mongoTestStatus, setMongoTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [mongoTestMessage, setMongoTestMessage] = useState('');
  const [lifecycleMcpKey, setLifecycleMcpKey] = useState<string | null>(null);
  const storageModeRefs = useRef<Record<StorageMode, HTMLButtonElement | null>>({
    shared: null,
    dedicated: null,
  });

  const applySafeAllowlistPreset = React.useCallback(() => {
    const preset = [
      // Minimal safe default: template reads only (no writes).
      { agent_id: 'contextualizer-registry', workflows: ['list_templates', 'get_template'] },
    ];
    setAgentsAllowAll(false);
    setAgentsEnforceAllowlist(true);
    setAgentsAllowlistJson(JSON.stringify(preset, null, 2));
  }, []);

  const clearAllowlist = React.useCallback(() => {
    setAgentsAllowlistJson('[]');
  }, []);

  const handleStorageModeKeyDown = React.useCallback((event: React.KeyboardEvent<HTMLButtonElement>, currentMode: StorageMode) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      setStorageMode(currentMode);
      return;
    }

    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      return;
    }

    event.preventDefault();
    const currentIndex = STORAGE_MODE_OPTIONS.findIndex((option) => option.id === currentMode);
    const delta = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
    const nextIndex = (currentIndex + delta + STORAGE_MODE_OPTIONS.length) % STORAGE_MODE_OPTIONS.length;
    const nextMode = STORAGE_MODE_OPTIONS[nextIndex].id;
    setStorageMode(nextMode);
    storageModeRefs.current[nextMode]?.focus();
  }, []);

  const q = useQuery({
    queryKey: ['app-instances', 'detail', app_id],
    queryFn: async () => getAppInstance(app_id),
    enabled: Boolean(app_id),
    staleTime: 5_000,
  });

  const qSettings = useQuery({
    queryKey: ['app-instances', 'settings', app_id],
    queryFn: async () => getAppSettings(app_id),
    enabled: Boolean(app_id),
    staleTime: 5_000,
  });

  const qMembers = useQuery({
    queryKey: ['app-instances', 'members', app_id],
    queryFn: async () => listAppMembers(app_id),
    enabled: Boolean(app_id && showAdvanced),
    staleTime: 5_000,
  });

  const AUDIT_PAGE_SIZE = 50;
  const qAudit = useInfiniteQuery({
    queryKey: ['app-instances', 'audit-events', app_id, auditType],
    queryFn: async ({ pageParam }) => {
      return listAppAuditEvents(app_id, {
        limit: AUDIT_PAGE_SIZE,
        type: auditType || undefined,
        before: pageParam ? String(pageParam) : undefined,
      });
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      const events = Array.isArray((lastPage as any)?.events) ? ((lastPage as any).events as any[]) : [];
      if (events.length < AUDIT_PAGE_SIZE) return undefined;
      const last = events[events.length - 1];
      const ts = last?.timestamp;
      if (!ts) return undefined;
      const d = new Date(ts);
      if (!Number.isFinite(d.getTime())) return undefined;
      return d.toISOString();
    },
    enabled: Boolean(app_id && showAdvanced),
    staleTime: 5_000,
  });

  const retry = useMutation({
    mutationFn: async () => retryProvisioning(app_id),
    onSuccess: () => q.refetch(),
  });

  const patch = useMutation({
    mutationFn: async () => updateAppInstance(app_id, { name: rename.trim() || undefined, description: desc.trim() || undefined }),
    onSuccess: () => q.refetch(),
  });

  const del = useMutation({
    mutationFn: async () => deleteAppInstance(app_id),
    onSuccess: () => navigate('/apps'),
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const createKey = useMutation({
    mutationFn: async () => {
      const scopes = newKeyScopes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      return createAppKey(app_id, { name: newKeyName.trim() || 'Key', scopes });
    },
    onSuccess: () => q.refetch(),
  });

  const revokeKey = useMutation({
    mutationFn: async (key_id: string) => revokeAppKey(app_id, key_id),
    onSuccess: () => q.refetch(),
  });

  const addMember = useMutation({
    mutationFn: async () => {
      const v = memberEmailOrId.trim();
      if (!v) throw new Error('Member email or user_id is required');
      const role = String(memberRole || 'viewer').trim();
      const isEmail = v.includes('@');
      return upsertAppMember(app_id, isEmail ? { email: v, role } : { user_id: v, role });
    },
    onSuccess: () => {
      setMemberEmailOrId('');
      setMemberRole('viewer');
      qMembers.refetch();
    },
  });

  const delMember = useMutation({
    mutationFn: async (user_id: string) => removeAppMember(app_id, user_id),
    onSuccess: () => qMembers.refetch(),
  });

  const createAdmin = useMutation({
    mutationFn: async () => createAdminUser({ email: adminEmail.trim().toLowerCase(), password: adminPassword }),
    onSuccess: (resp) => {
      setAdminCreateMessage(`Admin user created: ${resp.email}`);
      setAdminEmail('');
      setAdminPassword('');
    },
    onError: (err: any) => {
      setAdminCreateMessage(err?.message || 'Failed to create admin user');
    },
  });

  const saveAgentsSettings = useMutation({
    mutationFn: async () => {
      let allowlist: any = [];
      try {
        allowlist = agentsAllowlistJson.trim() ? JSON.parse(agentsAllowlistJson) : [];
      } catch (e: any) {
        throw new Error(`Invalid allowlist JSON: ${e?.message || 'parse_error'}`);
      }
      let workflowOverrides: any = {};
      try {
        workflowOverrides = agentsWorkflowOverridesJson.trim() ? JSON.parse(agentsWorkflowOverridesJson) : {};
      } catch (e: any) {
        throw new Error(`Invalid workflow_overrides JSON: ${e?.message || 'parse_error'}`);
      }
      const woErrors = validateWorkflowOverrides(workflowOverrides);
      if (woErrors.length) {
        throw new Error(`Invalid workflow_overrides:\n- ${woErrors.slice(0, 8).join('\n- ')}`);
      }
      const perMin = Number(agentsQuotaPerMin);
      if (!Number.isFinite(perMin) || perMin <= 0) {
        throw new Error('Executions/minute must be a positive number');
      }
      const perDay = Number(agentsQuotaPerDay);
      if (!Number.isFinite(perDay) || perDay < 0) {
        throw new Error('Executions/day must be a number ≥ 0');
      }
      const tokensPerDay = Number(agentsTokensPerDay);
      if (!Number.isFinite(tokensPerDay) || tokensPerDay < 0) {
        throw new Error('Tokens/day must be a number ≥ 0');
      }
      return updateAppSettings(app_id, {
        agents: {
          enforce_allowlist: Boolean(agentsEnforceAllowlist),
          allow_all: Boolean(agentsAllowAll),
          allowlist,
          quotas: {
            executions_per_minute: perMin,
            ...(perDay > 0 ? { executions_per_day: perDay } : {}),
            ...(tokensPerDay > 0 ? { tokens_per_day: tokensPerDay } : {}),
            ...(workflowOverrides && typeof workflowOverrides === 'object' ? { workflow_overrides: workflowOverrides } : {}),
          },
        },
      });
    },
    onSuccess: () => qSettings.refetch(),
  });

  const saveBackendBuilderSettings = useMutation({
    mutationFn: async () => {
      const buildDedicatedUri = () => {
        const raw = dedicatedMongoUri.trim();
        if (raw) return raw;
        if (!extMongoHost.trim()) return '';
        const protocol = extMongoUseSrv ? 'mongodb+srv' : 'mongodb';
        const userPart = extMongoUser.trim()
          ? `${encodeURIComponent(extMongoUser.trim())}:${encodeURIComponent(extMongoPass)}@`
          : '';
        const dbPart = extMongoDb.trim() ? `/${encodeURIComponent(extMongoDb.trim())}` : '';
        return `${protocol}://${userPart}${extMongoHost.trim()}${dbPart}?retryWrites=true&w=majority`;
      };
      let secretRef = String((qSettings.data as any)?.settings?.data?.secret_ref || '').trim() || null;
      if (storageMode === 'dedicated') {
        const finalUri = buildDedicatedUri();
        if (finalUri && mongoTestStatus !== 'success') {
          throw new Error('Test Mongo connection successfully before saving dedicated mode.');
        }
        if (finalUri) {
          const stored = await setAppDedicatedMongoUri(app_id, {
            env: 'dev',
            value: finalUri,
            reason: `backend_builder:${app_id}:dedicated_mongodb`,
          });
          if (stored?.secret_ref) secretRef = String(stored.secret_ref);
        }
      }
      return updateAppSettings(app_id, {
        modules: {
          mapper_enabled: mapperModuleEnabled,
          asset_enabled: assetModuleEnabled,
        },
        data: {
          storage_mode: storageMode,
          secret_ref: storageMode === 'dedicated' ? secretRef : null,
          ext_mongo_host: storageMode === 'dedicated' ? extMongoHost.trim() || null : null,
          ext_mongo_user: storageMode === 'dedicated' ? extMongoUser.trim() || null : null,
          ext_mongo_db: storageMode === 'dedicated' ? extMongoDb.trim() || null : null,
          ext_mongo_use_srv: storageMode === 'dedicated' ? extMongoUseSrv : null,
        },
      });
    },
    onSuccess: () => {
      setDedicatedMongoUri('');
      setExtMongoPass('');
      qSettings.refetch();
    },
  });

  useEffect(() => {
    if (mongoTestStatus === 'idle') return;
    setMongoTestStatus('idle');
    setMongoTestMessage('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageMode, dedicatedMongoUri, extMongoHost, extMongoUser, extMongoPass, extMongoDb, extMongoUseSrv]);

  const app = q.data || null;
  const keys = Array.isArray((q.data as any)?.keys) ? ((q.data as any).keys as any[]) : [];
  const members = Array.isArray((qMembers.data as any)?.members) ? ((qMembers.data as any).members as any[]) : [];
  const workflowOverridesParsed = useMemo(() => {
    if (!agentsWorkflowOverridesJson.trim()) return {};
    try {
      return JSON.parse(agentsWorkflowOverridesJson);
    } catch {
      return null;
    }
  }, [agentsWorkflowOverridesJson]);
  const workflowOverrideErrors = workflowOverridesParsed
    ? validateWorkflowOverrides(workflowOverridesParsed)
    : ['workflow_overrides JSON is invalid'];
  const allowlistParsed = useMemo(() => {
    if (!agentsAllowlistJson.trim()) return [];
    try {
      return JSON.parse(agentsAllowlistJson);
    } catch {
      return null;
    }
  }, [agentsAllowlistJson]);

  // Keep the global app context in sync when viewing details.
  React.useEffect(() => {
    if (app_id && app?.name) setApp({ appId: app_id, appName: String(app.name) });
  }, [app_id, app?.name, setApp]);

  // Initialize the local editor state from settings payload
  React.useEffect(() => {
    const s = (qSettings.data as any)?.settings?.agents;
    if (!s) return;
    setAgentsAllowAll(Boolean(s.allow_all));
    setAgentsEnforceAllowlist(Boolean(s.enforce_allowlist));
    const qpm = s?.quotas?.executions_per_minute;
    if (qpm != null) setAgentsQuotaPerMin(String(qpm));
    const qpd = s?.quotas?.executions_per_day;
    if (qpd != null) setAgentsQuotaPerDay(String(qpd));
    const tpd = s?.quotas?.tokens_per_day;
    if (tpd != null) setAgentsTokensPerDay(String(tpd));
    const wfo = s?.quotas?.workflow_overrides;
    if (wfo != null) {
      try {
        setAgentsWorkflowOverridesJson(JSON.stringify(wfo, null, 2));
      } catch {
        // ignore
      }
    }
    const al = s?.allowlist;
    if (al != null) {
      try {
        setAgentsAllowlistJson(JSON.stringify(al, null, 2));
      } catch {
        // ignore
      }
    }

    const modules = (qSettings.data as any)?.settings?.modules || {};
    setMapperModuleEnabled(Boolean(modules.mapper_enabled));
    setAssetModuleEnabled(Boolean(modules.asset_enabled));
    const dataSettings = (qSettings.data as any)?.settings?.data || {};
    setStorageMode(String(dataSettings.storage_mode || 'shared').toLowerCase() === 'dedicated' ? 'dedicated' : 'shared');
    if (dataSettings.ext_mongo_host) setExtMongoHost(dataSettings.ext_mongo_host);
    if (dataSettings.ext_mongo_user) setExtMongoUser(dataSettings.ext_mongo_user);
    if (dataSettings.ext_mongo_db) setExtMongoDb(dataSettings.ext_mongo_db);
    if (dataSettings.ext_mongo_use_srv != null) setExtMongoUseSrv(Boolean(dataSettings.ext_mongo_use_srv));
  }, [qSettings.data]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="p-4 space-y-4 w-full">
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" onClick={() => navigate('/apps')} className="shadow-sm">
            ← Back
          </Button>
          <Button variant="outline" onClick={() => q.refetch()} disabled={q.isFetching} className="shadow-sm">
            {q.isFetching ? '↻ Refreshing…' : '↻ Refresh'}
          </Button>
        </div>

        <div className="space-y-4 w-full">
            <h2 className="text-sm font-semibold text-slate-700">Connect</h2>
            <McpConnectionCard
              appId={app_id}
              appName={q.data?.name || app_id}
              mcpSettings={(qSettings.data as any)?.settings?.mcp}
              mcpKeyId={null}
              keys={(q.data as any)?.keys || []}
              onRefresh={() => { void q.refetch(); void qSettings.refetch(); }}
              onMcpKeyChange={setLifecycleMcpKey}
            />
            <h2 className="text-sm font-semibold text-slate-700">Sign-in & hosting</h2>
            {String((q.data as any)?.tenant_id || '').trim() ? (
              <>
            <AppLifecyclePanel
              appId={app_id}
              appName={q.data?.name || app_id}
              tenantId={String((q.data as any).tenant_id)}
              mcpKey={lifecycleMcpKey}
              settings={qSettings.data}
              onRefresh={() => qSettings.refetch()}
            />
            <AppHostingCard
              appId={app_id}
              tenantId={String((q.data as any).tenant_id)}
              hostingSettings={(qSettings.data as any)?.settings?.hosting}
              onRefresh={() => qSettings.refetch()}
            />
            {(() => {
              const settings = (qSettings.data as any)?.settings || {};
              const registration = settings?.hosting?.public_registration;
              const extra = settings?.hosting?.env_config_extra;
              const firebaseName = extra && typeof extra.FIREBASE_APP_NAME === 'string' ? extra.FIREBASE_APP_NAME.trim() : '';
              const domains = Array.isArray(registration?.allowed_origins)
                ? registration.allowed_origins.map((item: unknown) => String(item || '').trim()).filter(Boolean)
                : [];
              const hasSignup = registration && typeof registration.enabled === 'boolean';
              if (!hasSignup && !firebaseName && domains.length === 0) return null;
              return (
                <div className="rounded border border-slate-200 bg-white p-3 text-sm text-slate-700 space-y-1">
                  {hasSignup ? <div>Self-signup: {registration.enabled ? 'on' : 'off'}</div> : null}
                  {firebaseName ? <div>Firebase app name: {firebaseName}</div> : null}
                  {domains.length ? <div>Sign-up allowed from: {domains.join(', ')}</div> : null}
                </div>
              );
            })()}
              </>
            ) : (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">This app has no tenant, so sign-in and hosting controls are hidden.</div>
            )}
            <ProvisioningStatusCard
              appId={app_id}
              appStatus={String((q.data as any)?.status || '')}
              provisioningError={(q.data as any)?.provisioning_error || null}
              provision={(qSettings.data as any)?.settings?.builder?.provision || null}
              onRefresh={() => {
                q.refetch();
                qSettings.refetch();
              }}
            />
        </div>

        <h2 className="text-sm font-semibold text-slate-700">Data & APIs</h2>
        <AppDataSourcesSummaryCard
          appId={app_id}
          dataSettings={(qSettings.data as any)?.settings?.data || null}
        />
        <AppApiConnectionsCard appId={app_id} />

        <h2 className="text-sm font-semibold text-slate-700">Activity</h2>
        <MailboxPanel appId={app_id} />
        <BuildSessionsTracker appId={app_id} appName={q.data?.name || app_id} />
        <ObservabilityPanel appId={app_id} />

        <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Data scope for PIM/DAM</div>
              <div className="text-xs text-gray-600">
                PIM/DAM only show assets for the active app context. Current: <span className="font-mono">{app_id}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setApp({ appId: app_id, appName: app?.name || null })}
                className="shadow-sm"
              >
                Use this app
              </Button>
            </div>
          </div>
        </div>

      {q.isLoading && <div className="text-sm text-gray-600 bg-white rounded-lg p-6">⏳ Loading…</div>}
      {q.error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-6">{(q.error as any)?.message || 'Failed to load app'}</div>}

      {app && (
        <>
          <Card className="shadow-lg border-gray-200 overflow-hidden">
            <CardContent className="p-0">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
      <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-2xl font-bold">{app.name}</div>
                    {app.description && (
                      <div
                        className="text-sm text-blue-100 mt-1"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {app.description}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={isSandboxExpired(app) ? 'expired' : String(app.status || 'unknown')} />
                    <StagedDeletionBadge app={app} />
                  </div>
                </div>
              </div>

            </CardContent>
          </Card>

          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">⚙️ Advanced Settings</div>
                <div className="text-xs text-gray-500 mt-1">API keys, quotas, members, and technical configuration</div>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowAdvanced((v) => !v)}
                className="shadow-sm"
              >
                {showAdvanced ? '▼ Hide advanced' : '▶ Show advanced'}
              </Button>
            </div>
          </div>

          {showAdvanced && (
        <>
          <h2 className="text-sm font-semibold text-red-700">Danger zone</h2>
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-semibold">{app.name}</div>
                <StatusBadge status={isSandboxExpired(app) ? 'expired' : String(app.status || 'unknown')} />
              </div>
              {app.description && <div className="text-sm text-gray-600">{app.description}</div>}
              {app.provisioning_error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  Provisioning error: {String(app.provisioning_error)}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" onClick={() => retry.mutate()} disabled={retry.isPending}>
                  {retry.isPending ? 'Retrying…' : 'Retry provisioning'}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    del.reset();
                    setDeleteDialogOpen(true);
                  }}
                  disabled={del.isPending}
                >
                  {del.isPending ? 'Deleting…' : 'Delete app'}
                </Button>
              </div>
            </CardContent>
          </Card>
          <AppDeleteConfirmationDialog
            open={deleteDialogOpen}
            target={{ appId: app_id, appName: app.name || app_id }}
            onOpenChange={(open) => {
              if (open) return;
              del.reset();
              setDeleteDialogOpen(false);
            }}
            onConfirm={() => del.mutate()}
            isPending={del.isPending}
            errorMessage={del.error ? ((del.error as any)?.message || 'Delete failed') : null}
          />

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="text-sm font-semibold">Rename / update</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Name</div>
                  <Input value={rename} onChange={(e) => setRename(e.target.value)} placeholder={app.name} />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Description</div>
                  <Input value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={app.description || 'Optional'} />
                </div>
              </div>
              <Button onClick={() => patch.mutate()} disabled={patch.isPending || (!rename.trim() && !desc.trim())}>
                {patch.isPending ? 'Saving…' : 'Save'}
              </Button>
              {patch.error && <div className="text-sm text-red-600">{(patch.error as any)?.message || 'Update failed'}</div>}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-4">
              <div>
                <div className="text-sm font-semibold">Modules</div>
                <div className="text-xs text-gray-600">Configure optional modules and per-app data routing.</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded p-3 bg-white">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Optional Modules</div>
                  <label className="flex items-center gap-2 text-sm mb-2">
                    <input type="checkbox" checked={mapperModuleEnabled} onChange={(e) => setMapperModuleEnabled(e.target.checked)} />
                    Mapper Module
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={assetModuleEnabled} onChange={(e) => setAssetModuleEnabled(e.target.checked)} />
                    Asset Module
                  </label>
                </div>
              </div>

              <div className="border rounded p-3 bg-white space-y-3">
                <div className="text-xs font-semibold text-gray-700">Database Strategy</div>
                <div role="radiogroup" aria-label="Database Strategy" className="space-y-2">
                  {STORAGE_MODE_OPTIONS.map((option) => {
                    const checked = storageMode === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        tabIndex={checked ? 0 : -1}
                        ref={(node) => {
                          storageModeRefs.current[option.id] = node;
                        }}
                        onClick={() => setStorageMode(option.id)}
                        onKeyDown={(event) => handleStorageModeKeyDown(event, option.id)}
                        className={`w-full rounded border px-3 py-2 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
                          checked
                            ? 'border-primary/40 bg-primary/10 text-foreground'
                            : 'border-border bg-background hover:bg-muted/50'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border ${checked ? 'border-primary' : 'border-muted-foreground/50'}`}>
                            <span className={`h-2 w-2 rounded-full ${checked ? 'bg-primary' : 'bg-transparent'}`} />
                          </span>
                          <span>
                            {option.label}
                            {option.description ? <span className="text-gray-400"> {option.description}</span> : null}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {storageMode === 'dedicated' && (
                  <div className="border rounded p-3 bg-gray-50 space-y-3 mt-1">
                    <div className="text-xs font-medium text-gray-600">
                      3rd-party MongoDB Connection <span className="text-gray-400">(credentials stored securely in Vault)</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Host / Cluster address</label>
                        <Input
                          value={extMongoHost}
                          onChange={(e) => setExtMongoHost(e.target.value)}
                          placeholder="cluster0.abc123.mongodb.net"
                        />
                        <div className="flex items-center gap-2 mt-1">
                          <label className="flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={extMongoUseSrv}
                              onChange={(e) => setExtMongoUseSrv(e.target.checked)}
                              className="rounded"
                            />
                            Use SRV record (mongodb+srv://)
                          </label>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Username</label>
                        <Input
                          value={extMongoUser}
                          onChange={(e) => setExtMongoUser(e.target.value)}
                          placeholder="db_user"
                          autoComplete="off"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Password</label>
                        <div className="relative">
                          <Input
                            type={showExtMongoPass ? 'text' : 'password'}
                            value={extMongoPass}
                            onChange={(e) => setExtMongoPass(e.target.value)}
                            placeholder="••••••••"
                            autoComplete="new-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowExtMongoPass(!showExtMongoPass)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                          >
                            {showExtMongoPass ? 'Hide' : 'Show'}
                          </button>
                        </div>
                      </div>

                      <div className="col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Database name <span className="text-gray-400">(optional)</span></label>
                        <Input
                          value={extMongoDb}
                          onChange={(e) => setExtMongoDb(e.target.value)}
                          placeholder="my_app_db"
                        />
                      </div>
                    </div>

                    <details className="text-xs text-gray-500">
                      <summary className="cursor-pointer hover:text-gray-700">Or paste a full connection string</summary>
                      <div className="mt-2">
                        <Input
                          type="password"
                          value={dedicatedMongoUri}
                          onChange={(e) => setDedicatedMongoUri(e.target.value)}
                          placeholder="mongodb+srv://user:pass@cluster.mongodb.net/db"
                        />
                        <div className="text-[11px] text-gray-400 mt-1">
                          Overrides the fields above if provided. If all fields are blank, existing connection is retained.
                        </div>
                      </div>
                    </details>

                    {(qSettings.data as any)?.settings?.data?.secret_ref && (
                      <div className="flex items-center gap-1.5 text-[11px] text-green-700 bg-green-50 rounded px-2 py-1">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        3rd-party database connected
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={mongoTestStatus === 'testing'}
                        onClick={async () => {
                          const raw = dedicatedMongoUri.trim();
                          const finalUri = raw
                            ? raw
                            : extMongoHost.trim()
                              ? `${extMongoUseSrv ? 'mongodb+srv' : 'mongodb'}://${extMongoUser.trim()
                                ? `${encodeURIComponent(extMongoUser.trim())}:${encodeURIComponent(extMongoPass)}@`
                                : ''}${extMongoHost.trim()}${extMongoDb.trim() ? `/${encodeURIComponent(extMongoDb.trim())}` : ''}?retryWrites=true&w=majority`
                              : '';
                          if (!finalUri) {
                            setMongoTestStatus('error');
                            setMongoTestMessage('Provide a Mongo URI or host details first.');
                            return;
                          }
                          setMongoTestStatus('testing');
                          setMongoTestMessage('Testing connection...');
                          try {
                            const tested = await testMongoConnection(finalUri);
                            if (tested?.ok) {
                              setMongoTestStatus('success');
                              setMongoTestMessage('Connection successful');
                            } else {
                              setMongoTestStatus('error');
                              setMongoTestMessage([tested?.code, tested?.detail].filter(Boolean).join(': ') || 'Connection failed');
                            }
                          } catch (err: any) {
                            setMongoTestStatus('error');
                            setMongoTestMessage(err?.message || 'Connection test failed');
                          }
                        }}
                      >
                        {mongoTestStatus === 'testing' ? 'Testing...' : 'Test connection'}
                      </Button>
                      {mongoTestStatus === 'success' && (
                        <div className="text-xs text-green-700">{mongoTestMessage}</div>
                      )}
                      {mongoTestStatus === 'error' && (
                        <div className="text-xs text-red-600">{mongoTestMessage}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={() => saveBackendBuilderSettings.mutate()} disabled={saveBackendBuilderSettings.isPending}>
                  {saveBackendBuilderSettings.isPending ? 'Saving…' : 'Save backend builder settings'}
                </Button>
                {saveBackendBuilderSettings.error && (
                  <div className="text-sm text-red-600">
                    {(saveBackendBuilderSettings.error as any)?.message || 'Save failed'}
                  </div>
                )}
                {saveBackendBuilderSettings.data && !saveBackendBuilderSettings.error && (
                  <div className="text-sm text-green-700">Saved.</div>
                )}
              </div>
            </CardContent>
          </Card>

          <McpHandoffCard appId={app_id} />

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">Agent guardrails (Public Agents)</div>
                  <div className="text-xs text-gray-600">
                    Controls <span className="font-mono">/public/agents/*</span> allowlist + quota for this app.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setAgentsQuotaPerMin(String(DEFAULT_AGENT_QUOTAS.executions_per_minute));
                      setAgentsQuotaPerDay(String(DEFAULT_AGENT_QUOTAS.executions_per_day));
                      setAgentsTokensPerDay(String(DEFAULT_AGENT_QUOTAS.tokens_per_day));
                    }}
                  >
                    Apply recommended quotas
                  </Button>
                  <Button variant="outline" onClick={() => qSettings.refetch()} disabled={qSettings.isFetching}>
                    Refresh
                  </Button>
                </div>
              </div>

              {qSettings.isLoading && <div className="text-sm text-gray-600">Loading settings…</div>}
              {qSettings.error && (
                <div className="text-sm text-red-600">{(qSettings.error as any)?.message || 'Failed to load settings'}</div>
              )}
              {agentsEnforceAllowlist && !agentsAllowAll && Array.isArray(allowlistParsed) && allowlistParsed.length === 0 && (
                <div className="text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-2">
                  Allowlist enforcement is enabled but the allowlist is empty. Public agent calls will be denied.
                </div>
              )}
              {allowlistParsed === null && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  Allowlist JSON is invalid. Fix before saving.
                </div>
              )}

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={agentsAllowAll}
                  onChange={(e) => setAgentsAllowAll(e.target.checked)}
                />
                <div className="text-sm">
                  Allow all agents (disable allowlist)
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={agentsEnforceAllowlist}
                  onChange={(e) => setAgentsEnforceAllowlist(e.target.checked)}
                />
                <div className="text-sm">
                  Enforce allowlist (deny if missing/empty)
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Executions per minute</div>
                  <Input value={agentsQuotaPerMin} onChange={(e) => setAgentsQuotaPerMin(e.target.value)} />
                  <div className="text-[11px] text-gray-500 mt-1">Hard rate limit for public agent execution.</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Executions per day (0 = disabled)</div>
                  <Input value={agentsQuotaPerDay} onChange={(e) => setAgentsQuotaPerDay(e.target.value)} />
                  <div className="text-[11px] text-gray-500 mt-1">Daily cap across all public agent calls.</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Tokens per day (0 = disabled)</div>
                  <Input value={agentsTokensPerDay} onChange={(e) => setAgentsTokensPerDay(e.target.value)} />
                  <div className="text-[11px] text-gray-500 mt-1">Best-effort token budget from Volcano response metadata.</div>
                </div>
                <div className="md:col-span-2">
                  <AgentAllowlistEditor
                    appId={app_id}
                    allowlist={(() => { try { return JSON.parse(agentsAllowlistJson); } catch { return []; } })()}
                    onSave={() => qSettings.refetch()}
                  />
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-600 mb-1">Workflow quota overrides (JSON)</div>
                <Textarea
                  value={agentsWorkflowOverridesJson}
                  onChange={(e) => setAgentsWorkflowOverridesJson(e.target.value)}
                  className="font-mono text-xs min-h-[120px]"
                />
                {workflowOverrideErrors.length > 0 && workflowOverridesParsed !== null && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">
                    {workflowOverrideErrors.slice(0, 4).map((err, idx) => (
                      <div key={idx}>{err}</div>
                    ))}
                  </div>
                )}
                <div className="text-xs text-gray-500 mt-1">
                  Key format: <span className="font-mono">{"<agent_id>:<workflow>"}</span> (wildcards allowed: <span className="font-mono">*</span>). Example:{' '}
                  <span className="font-mono">
                    {'{'}"contextualizer-registry:list_templates": {'{'}"executions_per_minute": 10, "tokens_per_day": 50000{'}'}{'}'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={() => saveAgentsSettings.mutate()} disabled={saveAgentsSettings.isPending}>
                  {saveAgentsSettings.isPending ? 'Saving…' : 'Save agent guardrails'}
                </Button>
                {saveAgentsSettings.error && (
                  <div className="text-sm text-red-600">
                    {(saveAgentsSettings.error as any)?.message || 'Save failed'}
                  </div>
                )}
                {saveAgentsSettings.data && !saveAgentsSettings.error && (
                  <div className="text-sm text-green-700">Saved.</div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── App Agents Section ── */}
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">App Agents</div>
                  <div className="text-xs text-gray-600">
                    Agents created by and scoped to this app instance. These agents run with <span className="font-mono">agent_type: app</span> guardrails (Tier-2 max).
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateAgentModal(true)}
                >
                  + Create Agent
                </Button>
                <CreateAgentModal
                  appId={app_id}
                  open={showCreateAgentModal}
                  onClose={() => setShowCreateAgentModal(false)}
                  onCreated={() => qSettings.refetch()}
                />
              </div>
              {/* App agents listing via allowlist */}
              {(() => {
                const al = (() => { try { return JSON.parse(agentsAllowlistJson); } catch { return []; } })();
                const appPrefixed = al.filter((a: any) => String(a.agent_id || '').startsWith(`${app_id}--`));
                if (appPrefixed.length === 0) {
                  return (
                    <div className="text-xs text-gray-500 py-4 text-center">
                      No agents created for this app yet. Click "Create Agent" to get started.
                    </div>
                  );
                }
                return (
                  <div className="space-y-2">
                    {appPrefixed.map((a: any) => (
                      <div key={a.agent_id} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
                        <div>
                          <div className="text-xs font-medium text-gray-900 font-mono">{a.agent_id}</div>
                          {a.workflows?.length > 0 && (
                            <div className="text-[10px] text-gray-500">
                              Workflows: {a.workflows.join(', ')}
                            </div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          className="text-xs h-7"
                          onClick={() => window.open(`/agents/${a.agent_id}`, '_blank')}
                        >
                          Open in Agent Hub
                        </Button>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">Audit events (Public Agents)</div>
                  <div className="text-xs text-gray-600">
                    Recent allowlist/quota decisions + executions for this <span className="font-mono">app_id</span>.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="hidden md:flex items-center gap-1">
                    <Button type="button" variant="outline" onClick={() => setAuditType('')}>All</Button>
                    <Button type="button" variant="outline" onClick={() => setAuditType('public_agents_execute_rate_limited')}>Rate limits</Button>
                    <Button type="button" variant="outline" onClick={() => setAuditType('public_agents_execute_denied')}>Denied</Button>
                    <Button type="button" variant="outline" onClick={() => setAuditType('public_agents_tokens_rate_limited')}>Token cap</Button>
                    <Button type="button" variant="outline" onClick={() => setAuditType('public_agents_tokens_budget_exceeded')}>Token exceeded</Button>
                  </div>
                  <Input
                    value={auditType}
                    onChange={(e) => setAuditType(e.target.value)}
                    placeholder="Filter by type (optional)"
                    className="w-64"
                  />
                  <Button variant="outline" onClick={() => qAudit.refetch()} disabled={qAudit.isFetching}>
                    Refresh
                  </Button>
                </div>
              </div>

              {qAudit.isLoading && <div className="text-sm text-gray-600">Loading audit events…</div>}
              {qAudit.error && <div className="text-sm text-red-600">{(qAudit.error as any)?.message || 'Failed to load audit events'}</div>}

              {!qAudit.isLoading && !qAudit.error && (
                <Table className="border rounded">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Agent</TableHead>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Decision / Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(
                      Array.isArray((qAudit.data as any)?.pages)
                        ? ((qAudit.data as any).pages as any[]).flatMap((p: any) => (Array.isArray(p?.events) ? p.events : []))
                        : []
                    ).map((ev: any, idx: number) => (
                      <TableRow key={`${ev.timestamp || ''}:${idx}`}>
                        <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : ''}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{String(ev.type || '')}</TableCell>
                        <TableCell className="text-xs font-mono">{String(ev.agent_id || '')}</TableCell>
                        <TableCell className="text-xs font-mono">{String(ev.workflow || '')}</TableCell>
                        <TableCell className="text-xs">
                          {ev.decision ? `decision=${String(ev.decision)}` : ''}
                          {ev.status_code != null ? `${ev.decision ? ' · ' : ''}status=${String(ev.status_code)}` : ''}
                          {ev.count != null && ev.limit_per_minute != null ? ` · ${String(ev.count)}/${String(ev.limit_per_minute)}/min` : ''}
                          {ev.limit_per_day != null ? ` · day_limit=${String(ev.limit_per_day)}` : ''}
                          {ev.quota_bucket ? ` · bucket=${String(ev.quota_bucket)}` : ''}
                          {ev.counter_key ? ` · counter=${String(ev.counter_key)}` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(
                      !Array.isArray((qAudit.data as any)?.pages) ||
                      ((qAudit.data as any).pages as any[]).flatMap((p: any) => (Array.isArray(p?.events) ? p.events : [])).length === 0
                    ) && (
                      <TableRow>
                        <TableCell className="text-sm text-gray-600" colSpan={5}>
                          No audit events found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              {!qAudit.isLoading && !qAudit.error && (
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Showing{' '}
                    {Array.isArray((qAudit.data as any)?.pages)
                      ? ((qAudit.data as any).pages as any[]).reduce((n: number, p: any) => n + (Array.isArray(p?.events) ? p.events.length : 0), 0)
                      : 0}{' '}
                    events
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => qAudit.fetchNextPage()}
                    disabled={!qAudit.hasNextPage || qAudit.isFetchingNextPage}
                  >
                    {qAudit.isFetchingNextPage ? 'Loading…' : qAudit.hasNextPage ? 'Load older' : 'No more'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold">Members</div>
                <Button variant="outline" onClick={() => qMembers.refetch()} disabled={qMembers.isFetching}>
                  Refresh
                </Button>
              </div>

              {qMembers.error && (
                <div className="text-sm text-red-600">{(qMembers.error as any)?.message || 'Failed to load members'}</div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <div className="text-xs text-gray-600 mb-1">Email address or user ID</div>
                  <Input
                    type="email"
                    value={memberEmailOrId}
                    onChange={(e) => setMemberEmailOrId(e.target.value)}
                    placeholder="e.g. someone@company.com"
                  />
                  <div className="text-xs text-gray-400 mt-1">If the email is not registered yet, a new user account will be created automatically.</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Role</div>
                  <select
                    value={memberRole}
                    onChange={(e) => setMemberRole(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                  >
                    <option value="viewer">Viewer — Read-only access</option>
                    <option value="member">Member — Standard access</option>
                    <option value="admin">Admin — Manage config &amp; members</option>
                    <option value="owner">Owner — Full control</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button onClick={() => addMember.mutate()} disabled={addMember.isPending || !memberEmailOrId.trim()}>
                  {addMember.isPending ? 'Adding…' : 'Add / update member'}
                </Button>
                {addMember.error && (
                  <div className="text-sm text-red-600">{(addMember.error as any)?.message || 'Failed to add member'}</div>
                )}
              </div>

              <Table className="border rounded">
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited by</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((m: any, idx: number) => {
                    const roleBadge: Record<string, string> = {
                      owner: 'bg-red-100 text-red-700',
                      admin: 'bg-orange-100 text-orange-700',
                      member: 'bg-blue-100 text-blue-700',
                      viewer: 'bg-gray-100 text-gray-600',
                    };
                    const roleTooltip: Record<string, string> = {
                      owner: 'Full ownership — can manage all settings, keys, and members',
                      admin: 'Administrative access — can manage members and configurations',
                      member: 'Standard access — can use app features and edit data',
                      viewer: 'Read-only access — can view but not modify data',
                    };
                    const rc = roleBadge[m.role] || 'bg-gray-100 text-gray-600';
                    return (
                      <TableRow key={`${m.user_id || ''}:${idx}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {m.photoURL ? (
                              <img src={m.photoURL} alt="" className="w-7 h-7 rounded-full flex-shrink-0" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold flex-shrink-0">
                                {(m.displayName || m.email || m.user_id || '?')[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-xs font-medium text-gray-900 truncate">{m.displayName || 'Unnamed'}</div>
                              <div className="text-xs text-gray-500 truncate">{m.email || m.user_id || ''}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${rc}`} title={roleTooltip[m.role] || ''}>
                            {String(m.role || 'viewer')}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500">{String(m.invited_by || '—')}</TableCell>
                        <TableCell className="text-xs text-gray-600">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => delMember.mutate(String(m.user_id || ''))}
                            disabled={delMember.isPending || !m.user_id}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {members.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-sm text-gray-600">
                        No members yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <EntityAccessCard appId={app_id} settings={qSettings.data} onRefresh={() => qSettings.refetch()} />

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="text-sm font-semibold">Create admin user</div>
              <div className="text-xs text-gray-600">
                Creates (or updates) a Firebase user with full admin roles.
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Email</div>
                  <Input
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@company.com"
                  />
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Password</div>
                  <Input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Min 6 characters"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => createAdmin.mutate()}
                  disabled={createAdmin.isPending || !adminEmail.trim() || adminPassword.length < 6}
                >
                  {createAdmin.isPending ? 'Creating…' : 'Create admin'}
                </Button>
                {adminCreateMessage && (
                  <div className={`text-sm ${adminCreateMessage.startsWith('Admin user created') ? 'text-green-600' : 'text-red-600'}`}>
                    {adminCreateMessage}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="text-sm font-semibold">API keys</div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Key name</div>
                  <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <div className="text-xs text-gray-600 mb-1">Scopes (comma-separated)</div>
                  <Input value={newKeyScopes} onChange={(e) => setNewKeyScopes(e.target.value)} placeholder="e.g. pim:read, dam:read, agents:execute" />
                </div>
              </div>
              <Button onClick={() => createKey.mutate()} disabled={createKey.isPending}>
                {createKey.isPending ? 'Creating…' : 'Create new key'}
              </Button>
              {createKey.data?.key && (
                <div className="mt-2 text-xs bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div className="font-semibold text-yellow-900 mb-1">New key (shown once)</div>
                  <div className="font-mono break-all">{createKey.data.key}</div>
                </div>
              )}
              {createKey.error && <div className="text-sm text-red-600">{(createKey.error as any)?.message || 'Create key failed'}</div>}

              <div className="border-t pt-3 space-y-2">
                {keys.length === 0 && <div className="text-sm text-gray-600">No keys yet.</div>}
                {keys.map((k) => (
                  <div key={k.key_id} className="flex items-center justify-between gap-3 border rounded p-2 bg-white">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{k.name || 'Key'}</div>
                      <div className="text-xs text-gray-500 font-mono">{k.key_id}</div>
                      <div className="text-xs text-gray-600">
                        scopes: {Array.isArray(k.scopes) && k.scopes.length ? k.scopes.join(', ') : '(none)'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={String(k.status || 'unknown')} />
                      <Button
                        variant="outline"
                        onClick={() => revokeKey.mutate(String(k.key_id))}
                        disabled={revokeKey.isPending || String(k.status || '').toLowerCase() !== 'active'}
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
            </>
          )}
        </>
      )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Entity Access Settings Card
   ═══════════════════════════════════════════ */

function EntityAccessCard({ appId, settings, onRefresh }: { appId: string; settings: any; onRefresh: () => void }) {
  const entityConfig = (settings as any)?.settings?.entity_config || {};

  const [enabled, setEnabled] = useState(Boolean(entityConfig.enabled));
  const [selfReg, setSelfReg] = useState(Boolean(entityConfig.self_registration));
  const [maxEntities, setMaxEntities] = useState(String(entityConfig.max_entities_per_app || 100));
  const [maxContacts, setMaxContacts] = useState(String(entityConfig.max_contacts_per_entity || 50));
  const [allowedTypes, setAllowedTypes] = useState<string[]>(entityConfig.allowed_entity_types || []);

  React.useEffect(() => {
    const ec = (settings as any)?.settings?.entity_config || {};
    setEnabled(Boolean(ec.enabled));
    setSelfReg(Boolean(ec.self_registration));
    setMaxEntities(String(ec.max_entities_per_app || 100));
    setMaxContacts(String(ec.max_contacts_per_entity || 50));
    setAllowedTypes(ec.allowed_entity_types || []);
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return updateAppSettings(appId, {
        entity_config: {
          enabled,
          self_registration: selfReg,
          max_entities_per_app: Number(maxEntities) || 100,
          max_contacts_per_entity: Number(maxContacts) || 50,
          allowed_entity_types: allowedTypes.length > 0 ? allowedTypes : null,
        },
      });
    },
    onSuccess: () => onRefresh(),
  });

  const ALL_ENTITY_TYPES = [
    'company', 'close_corporation', 'trust', 'cooperative', 'non_profit',
    'individual', 'farm_operation', 'manufacturer', 'brand', 'service_provider',
    'government_entity', 'estate',
  ];

  const toggleType = (type: string) => {
    setAllowedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Entity Access</div>
            <div className="text-xs text-gray-600">
              Configure how entities and contacts interact with this app instance.
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable Entity Management</span>
              <p className="text-xs text-gray-500">Allow entities to be linked to this app</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={selfReg}
              onChange={(e) => setSelfReg(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Allow Self-Registration</span>
              <p className="text-xs text-gray-500">Contacts can register themselves and create entities</p>
            </div>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-gray-600 mb-1">Max entities per app</div>
              <Input
                type="number"
                value={maxEntities}
                onChange={(e) => setMaxEntities(e.target.value)}
                min={1}
                max={10000}
              />
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Max contacts per entity</div>
              <Input
                type="number"
                value={maxContacts}
                onChange={(e) => setMaxContacts(e.target.value)}
                min={1}
                max={1000}
              />
            </div>
          </div>

          <div>
            <div className="text-xs text-gray-600 mb-2">Allowed entity types (leave empty for all)</div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_ENTITY_TYPES.map((type) => {
                const isSelected = allowedTypes.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors ${
                      isSelected
                        ? 'bg-blue-100 text-blue-700 border-blue-300'
                        : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {type.replace(/_/g, ' ')}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save entity settings'}
          </Button>
          {saveMutation.error && (
            <div className="text-sm text-red-600">{(saveMutation.error as any)?.message || 'Save failed'}</div>
          )}
          {saveMutation.isSuccess && !saveMutation.error && (
            <div className="text-sm text-green-700">Saved.</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AppInstancesPage() {
  return (
    <Routes>
      <Route index element={<AppList />} />
      <Route path=":appId" element={<AppDetail />} />
      <Route path="*" element={<Navigate to="/apps" replace />} />
    </Routes>
  );
}

